/**
 * useVaultSearch — Fuse-backed search with an IndexedDB warm cache.
 *
 * On mount: tries `loadCachedIndex` for the slug; if the checksum
 * matches the current notes set, rehydrates it; otherwise builds a
 * fresh index off the main thread (via a microtask), then persists.
 *
 * Returns a `{ ready, hits, query, setQuery }` shape so the consumer
 * controls the query string and re-renders only when needed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ParsedNote } from '../../lib/vault/parse';
import {
  buildSearchIndex,
  loadSearchIndex,
  type SearchHit,
  type VaultSearchIndex,
} from '../../lib/vault/searchIndex';
import {
  loadCachedIndex,
  notesChecksum,
  saveCachedIndex,
} from '../../lib/vault/indexCache';

export interface UseVaultSearchResult {
  ready: boolean;
  query: string;
  setQuery: (q: string) => void;
  hits: SearchHit[];
}

export function useVaultSearch(
  clientSlug: string,
  notes: ParsedNote[],
): UseVaultSearchResult {
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const indexRef = useRef<VaultSearchIndex | null>(null);

  const checksum = useMemo(
    () =>
      notesChecksum(
        notes.map((n) => ({
          path: n.path,
          etagOrLen: (n as ParsedNote).body.length,
        })),
      ),
    [notes],
  );

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    indexRef.current = null;

    (async () => {
      // Try warm cache first.
      const cached = await loadCachedIndex(clientSlug);
      if (!cancelled && cached && cached.checksum === checksum) {
        const idx = loadSearchIndex({
          items: cached.items,
          index: cached.fuseIndex,
        });
        if (idx) {
          indexRef.current = idx;
          setReady(true);
          return;
        }
      }

      // Cold build — yield first so we don't block the initial paint.
      await new Promise((r) => setTimeout(r, 0));
      if (cancelled) return;
      const fresh = buildSearchIndex(notes);
      indexRef.current = fresh;
      setReady(true);

      // Persist for next time. Fire-and-forget; errors are swallowed
      // by indexCache (private mode, quota, etc.).
      const serialized = fresh.serialize();
      void saveCachedIndex({
        clientSlug,
        checksum,
        items: serialized.items,
        fuseIndex: serialized.index,
        savedAt: Date.now(),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [clientSlug, notes, checksum]);

  const hits = useMemo(() => {
    if (!ready || !indexRef.current) return [];
    return indexRef.current.query(query, 12);
  }, [query, ready]);

  return {
    ready,
    query,
    setQuery: useCallback((q: string) => setQuery(q), []),
    hits,
  };
}
